import React, { createContext, useContext, ReactNode } from 'react';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';

export const AuthContext = createContext<any>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Safely access environment variables
  const env = (import.meta as any).env || {};
  const domain = env.VITE_AUTH0_DOMAIN;
  const clientId = env.VITE_AUTH0_CLIENT_ID;

  if (domain && clientId) {
    return (
      <Auth0Provider
        domain={domain}
        clientId={clientId}
        authorizationParams={{
          redirect_uri: window.location.origin,
        }}
        onRedirectCallback={(appState) => {
          if (appState && appState.returnTo === '/admin') {
            window.location.hash = 'admin';
          }
        }}
      >
        <Auth0Bridge>{children}</Auth0Bridge>
      </Auth0Provider>
    );
  }

  // Fallback mock authentication for when keys are missing
  const mockAuth = {
    isAuthenticated: false,
    isLoading: false,
    user: undefined,
    logout: () => { window.location.reload(); },
    loginWithRedirect: async () => {
      alert("Authentication is not configured. Please set VITE_AUTH0_DOMAIN and VITE_AUTH0_CLIENT_ID in your environment variables to enable admin access.");
    },
    getAccessTokenSilently: async () => '',
  };

  return <AuthContext.Provider value={mockAuth}>{children}</AuthContext.Provider>;
};

const Auth0Bridge: React.FC<{ children: ReactNode }> = ({ children }) => {
  const auth = useAuth0();
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};
